import React, { useState } from "react";
import dayjs from "dayjs";
import { truncate } from "lodash";
import { useIntl } from "react-intl";
import classNames from "classnames";

import { Request } from "types/requests";
import {
    PaginationButtons,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "components/Table";
import styles from "./MyRequests.module.scss";
import MarkdownContent from "components/Markdown";
import useBreakPoint from "hooks/useBreakPoint";
import { Breakpoint } from "types/breakpoint";

import { ReactComponent as PlusIcon } from "static/icons/plus-circle.svg";
import { ReactComponent as MinusIcon } from "static/icons/minus-circle.svg";
import Button from "components/Buttons/Button";
import RequestComments from "./RequestComments";

type RequestRowProps = Request

const RequestRow: React.FC<RequestRowProps> = ({
    issueId,
    timestamp,
    summary,
    status,
    requestType,
    description,
    issueType,
}) => {
    const intl = useIntl();
    const [isExpanded, setExpanded] = useState(false);
    const toggleExpanded = () => setExpanded(!isExpanded);
    const [isCommentsShown,setIsCommentsShown] = useState<boolean>(false)
    const formatText = (txt?: string) => {
        if (!txt) {
            return "Not Selected";
        }
        return txt.charAt(0).toUpperCase() + txt.slice(1).split("_").join(" ");
    };

    const showRequestComments = () => {
        setIsCommentsShown(true);
    }

    const closeRequestConmentsModal = () => {
        setIsCommentsShown(false)
    }

    const cellStyle = isExpanded
        ? {
              border: "none",
          }
        : undefined;

    const ticketTimeStamp = new Date(timestamp);
    const now = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(now.getFullYear() - 1);
    const isTicketOverOneYear = ticketTimeStamp < oneYearAgo;

    const isSmallMobile = useBreakPoint(Breakpoint.smallMobile);
    return (
        <TableRow key={issueId} onClick={toggleExpanded}>
            {description ? (
                <TableCell className={styles["expand-button"]} style={cellStyle}>
                    {isExpanded ? <MinusIcon /> : <PlusIcon />}
                </TableCell>
            ) : (
                <TableCell />
            )}
            {isSmallMobile ? (
                <>
                    <TableCell style={cellStyle}>{issueId}</TableCell>
                    <TableCell style={cellStyle}>{formatText(issueType || requestType)}</TableCell>
                    <TableCell style={cellStyle}>
                        <span
                            className={classNames(styles["status"], {
                                [styles["status--open"]]:
                                    status ===
                                        intl.formatMessage({
                                            id: "case_management.zendesk_status.new",
                                        }) ||
                                    status ===
                                        intl.formatMessage({
                                            id: "case_management.zendesk_status.open",
                                        }),
                                [styles["status--in-progress"]]:
                                    status ===
                                        intl.formatMessage({
                                            id: "case_management.zendesk_status.pending",
                                        }) ||
                                    status ===
                                        intl.formatMessage({
                                            id: "case_management.zendesk_status.hold",
                                        }),
                                [styles["status--resolved"]]:
                                    status ===
                                    intl.formatMessage({
                                        id: "case_management.zendesk_status.solved",
                                    }),
                                [styles["status--closed"]]:
                                    status ===
                                    intl.formatMessage({
                                        id: "case_management.zendesk_status.closed",
                                    }),
                                [styles["status--not-found"]]: status === "Not Found",
                            })}
                        >
                            {status}
                        </span>
                    </TableCell>
                </>
            ) : (
                <>
                    <TableCell style={cellStyle}>{issueId}</TableCell>
                    <TableCell style={cellStyle}>
                        {dayjs(timestamp).format("YYYY-MM-DD / HH:mm")}
                    </TableCell>
                    <TableCell style={cellStyle}>{formatText(issueType || requestType)}</TableCell>
                    <TableCell
                        style={{
                            whiteSpace: "pre-wrap",
                            ...cellStyle,
                        }}
                    >
                        {truncate(formatText(summary), {
                            length: 100,
                            separator: " ",
                        })}
                    </TableCell>
                    <TableCell style={cellStyle}>
                        <span
                            className={classNames(styles["status"], {
                                [styles["status--open"]]:
                                    status ===
                                        intl.formatMessage({
                                            id: "case_management.zendesk_status.new",
                                        }) ||
                                    status ===
                                        intl.formatMessage({
                                            id: "case_management.zendesk_status.open",
                                        }),
                                [styles["status--in-progress"]]:
                                    status ===
                                        intl.formatMessage({
                                            id: "case_management.zendesk_status.pending",
                                        }) ||
                                    status ===
                                        intl.formatMessage({
                                            id: "case_management.zendesk_status.hold",
                                        }),
                                [styles["status--resolved"]]:
                                    status ===
                                    intl.formatMessage({
                                        id: "case_management.zendesk_status.solved",
                                    }),
                                [styles["status--closed"]]:
                                    status ===
                                    intl.formatMessage({
                                        id: "case_management.zendesk_status.closed",
                                    }),
                                [styles["status--not-found"]]: status === "Not Found",
                            })}
                        >
                            {status}
                        </span>
                    </TableCell>
                </>
            )}
            {isExpanded ? (
                <TableCell
                    className={styles["request-description"]}
                    onClick={(e) => e.stopPropagation()}
                >
                    {isExpanded && isSmallMobile ? (
                        <>
                            <h4 className={styles["content_mobile_text_header"]}>
                                {intl.formatMessage({
                                    id: "case_management.request_table.summary",
                                })}
                            </h4>
                            <MarkdownContent
                                content={summary}
                                className={styles["content_mobile_text"]}
                            />
                            <h4 className={styles["content_mobile_text_header"]}>
                                {intl.formatMessage({
                                    id: "case_management.request_table.timestamp",
                                })}
                            </h4>
                            <MarkdownContent
                                className={styles["content_mobile_text"]}
                                content={dayjs(timestamp).format("YYYY-MM-DD / HH:mm")}
                            />
                            <h4 className={styles["content_mobile_text_header"]}>
                                {intl.formatMessage({
                                    id: "case_management.request_table.description",
                                })}
                            </h4>
                            <MarkdownContent
                                className={styles["content_mobile_text"]}
                                content={description}
                            />
                            {!isTicketOverOneYear &&
                                <Button
                                text={intl.formatMessage({
                                    id: "case_management.request_table.view_full_conversation",
                                })}
                                size="small"
                                onClick={showRequestComments}/>
                            }
                        </>
                    ) : (
                        <>
                            <h4>
                                {intl.formatMessage({
                                    id: "case_management.request_table.description",
                                })}
                            </h4>
                            <MarkdownContent content={description} />
                            {!isTicketOverOneYear &&
                                <Button
                                text={intl.formatMessage({
                                    id: "case_management.request_table.view_full_conversation",
                                })}
                                size="medium"
                                onClick={showRequestComments}/>
                            }
                        </>
                    )}
                </TableCell>
            ) : null}
            {isCommentsShown && <RequestComments requestId={issueId} isVisible={isCommentsShown} onCloseModal={closeRequestConmentsModal} />}
        </TableRow>
    );
};

interface MyRequestsProps {
    userRequests: Request[];
    isNextPage: boolean;
    onLoadMoreRequests: () => void;
    isLoadingMore: boolean
 }

export const MyRequests: React.FC<MyRequestsProps> = ({ userRequests, isNextPage, onLoadMoreRequests, isLoadingMore }) => {
    const intl = useIntl();
    const [currentPage, setCurrentPage] = useState(0);
    const rowsPerPage = 5;
    const currentOffset = currentPage * rowsPerPage;

    const totalPages = Math.ceil(userRequests.length / rowsPerPage);
    const dataToShow = userRequests.slice(currentOffset, currentOffset + rowsPerPage);
    const canGoNextPage = currentPage < totalPages - 1;
    const canGoPreviousPage = currentPage > 0;

    const [pageEnd, setPageEnd] = useState(dataToShow.length);
    const [pageStart, setPageStart] = useState(1);

    const ToNextPage = () => {
        canGoNextPage && setCurrentPage(currentPage + 1);
        setPageEnd(pageEnd + rowsPerPage);
        setPageStart(pageStart + rowsPerPage);
    };
    const ToPreviousPage = () => {
        canGoPreviousPage && setCurrentPage(currentPage - 1);
        setPageEnd(pageEnd - rowsPerPage);
        setPageStart(pageStart - rowsPerPage);
    };
    const isMobile = window.innerWidth <= 415;
    return (
        <>
            <div className={styles["my-request"]}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            {isMobile ? (
                                <>
                                    <TableCell>
                                        {intl.formatMessage({
                                            id: "case_management.request_table.case_id",
                                        })}
                                    </TableCell>
                                    <TableCell>
                                        {intl.formatMessage({
                                            id: "case_management.request_table.request_type",
                                        })}
                                    </TableCell>
                                    <TableCell>
                                        {intl.formatMessage({
                                            id: "case_management.request_table.status",
                                        })}
                                    </TableCell>
                                </>
                            ) : (
                                <>
                                    <TableCell>
                                        {intl.formatMessage({
                                            id: "case_management.request_table.case_id",
                                        })}
                                    </TableCell>
                                    <TableCell>
                                        {intl.formatMessage({
                                            id: "case_management.request_table.timestamp",
                                        })}
                                    </TableCell>
                                    <TableCell>
                                        {intl.formatMessage({
                                            id: "case_management.request_table.request_type",
                                        })}
                                    </TableCell>
                                    <TableCell>
                                        {intl.formatMessage({
                                            id: "case_management.request_table.summary",
                                        })}
                                    </TableCell>
                                    <TableCell>
                                        {intl.formatMessage({
                                            id: "case_management.request_table.status",
                                        })}
                                    </TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dataToShow.map((requestData) => (
                            <RequestRow {...requestData} key={requestData.issueId} />
                        ))}
                    </TableBody>
                </Table>
            </div>
            {totalPages > 1 && (
                <PaginationButtons
                    maxCountReq={userRequests.length}
                    startOfPage={pageStart}
                    endOfPage={pageEnd}
                    next={ToNextPage}
                    previous={ToPreviousPage}
                    disableLeftArrow={!canGoPreviousPage}
                    disableRightArrow={!canGoNextPage}
                />
            )}
            {isNextPage && !canGoNextPage && (
                <div className={styles["load-more-button-container"]}>
                    <Button text={"Load More Tickets"} onClick={onLoadMoreRequests} variant="secondary" size="small" className={styles["load-more-button"]} loading={isLoadingMore}/>
                </div>

            )}
        </>
    );
};
